import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatPayout } from "@/lib/utils";

const BOOKING_STATUS_LABELS = {
  offered:   { label: "Offer pending",       cls: "border-amber-300/60 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300" },
  accepted:  { label: "Accepted",            cls: "border-emerald-300/60 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300" },
  signed:    { label: "Contract signed",     cls: "border-zinc-300/60 bg-zinc-100 text-zinc-700 dark:border-white/[0.1] dark:bg-white/[0.06] dark:text-zinc-300" },
  cancelled: { label: "Cancelled",           cls: "border-zinc-300/60 bg-zinc-100 text-zinc-500 dark:border-white/[0.08] dark:bg-white/[0.04] dark:text-zinc-400" },
  declined:  { label: "Declined",            cls: "border-red-300/60 bg-red-50 text-red-600 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400" },
  countered: { label: "Counter-offer sent",  cls: "border-amber-300/60 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300" },
};

const textarea = "w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder-zinc-400 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30 dark:border-white/[0.08] dark:bg-white/[0.05] dark:text-zinc-100";

export default function ThreadModal({
  isClient,
  activeThreadId,
  threadMessages,
  threadDraft,
  isPostingThreadMessage,
  isThreadLoading,
  threadBooking,
  showBookingForm,
  bookingForm,
  isBookingAction,
  currentUser,
  canCreateBooking,
  canAcceptBooking,
  canDeclineBooking,
  canSignContract,
  canPayDeposit,
  canCancelBooking,
  canReopenBooking,
  canCounterOffer,
  venueSigned,
  artistSigned,
  depositPaid,
  depositAmount,
  onClose,
  onSendMessage,
  onDraftChange,
  onBookingFieldChange,
  onCreateBookingOffer,
  onRespondToBooking,
  onSignContract,
  onPayDeposit,
  onSetShowBookingForm,
  showCounterForm,
  counterForm,
  onOpenCounterForm,
  onSetShowCounterForm,
  onCounterFieldChange,
  onSubmitCounterOffer,
}) {
  const messagesEndRef = useRef(null);
  const [acceptConfirm, setAcceptConfirm] = useState(false);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [threadMessages]);

  if (!isClient || !activeThreadId) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 py-8"
      onClick={onClose}
    >
      <div
        className="panel-strong max-h-[90vh] w-full max-w-lg overflow-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
            Conversation
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-zinc-200/60 p-1.5 text-zinc-400 transition hover:text-zinc-600 dark:border-white/[0.07] dark:hover:text-zinc-200"
            aria-label="Close"
          >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M1 1l12 12M13 1L1 13"/>
            </svg>
          </button>
        </div>

        {/* Booking panel */}
        {!isThreadLoading && (threadBooking || canCreateBooking) && (
          <div className="mb-4 rounded-xl border border-zinc-200/60 bg-zinc-50 p-4 text-sm dark:border-white/[0.07] dark:bg-white/[0.03]">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">
                  Booking
                </p>
                {threadBooking ? (
                  <div className="mt-1">
                    {(() => {
                      const s = BOOKING_STATUS_LABELS[threadBooking.status];
                      return s ? (
                        <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${s.cls}`}>
                          {s.label}
                        </span>
                      ) : (
                        <span className="text-xs text-zinc-400">{threadBooking.status}</span>
                      );
                    })()}
                  </div>
                ) : (
                  <p className="mt-0.5 text-xs text-zinc-400 dark:text-zinc-500">
                    Create a booking offer for this artist.
                  </p>
                )}
              </div>
              {threadBooking && (
                <div className="flex flex-wrap gap-1.5 text-xs">
                  <span className={`rounded-md border px-2 py-0.5 ${venueSigned ? "border-emerald-300/60 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300" : "border-zinc-200/60 text-zinc-400 dark:border-white/[0.07] dark:text-zinc-500"}`}>
                    Venue {venueSigned ? "✓" : "pending"}
                  </span>
                  <span className={`rounded-md border px-2 py-0.5 ${artistSigned ? "border-emerald-300/60 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300" : "border-zinc-200/60 text-zinc-400 dark:border-white/[0.07] dark:text-zinc-500"}`}>
                    Artist {artistSigned ? "✓" : "pending"}
                  </span>
                </div>
              )}
            </div>

            {threadBooking && (
              <div className="mt-3 space-y-1 text-xs text-zinc-500 dark:text-zinc-400">
                <div>Title: <span className="text-zinc-900 dark:text-zinc-100 font-medium">{threadBooking.title}</span></div>
                <div>
                  Date:{" "}
                  <span className="text-zinc-900 dark:text-zinc-100 font-medium">
                    {threadBooking.event_date
                      ? new Date(threadBooking.event_date).toLocaleDateString(undefined, { dateStyle: "medium" })
                      : "TBD"}
                  </span>
                </div>
                {threadBooking.city && (
                  <div>City: <span className="text-zinc-900 dark:text-zinc-100 font-medium">{threadBooking.city}</span></div>
                )}
                {Number.isFinite(threadBooking.payout) && (
                  <div>Payout: <span className="text-zinc-900 dark:text-zinc-100 font-medium">{formatPayout(threadBooking.payout)}</span></div>
                )}
                {depositAmount != null && (
                  <div className="flex items-center gap-2">
                    <span>Deposit: <span className="text-zinc-900 dark:text-zinc-100 font-medium">{formatPayout(Number(depositAmount))}</span></span>
                    <span className={`rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${depositPaid ? "border-emerald-300/60 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:text-emerald-300" : "border-zinc-200/60 text-zinc-400 dark:border-white/[0.07]"}`}>
                      {depositPaid ? "paid" : "unpaid"}
                    </span>
                  </div>
                )}
                {threadBooking.contract?.terms?.notes && (
                  <div className="pt-2">
                    <p className="font-medium text-zinc-700 dark:text-zinc-300">Notes</p>
                    <p className="whitespace-pre-wrap">{threadBooking.contract.terms.notes}</p>
                  </div>
                )}
                {threadBooking.contract?.terms?.cancellation_policy && (
                  <div className="pt-2">
                    <p className="font-medium text-zinc-700 dark:text-zinc-300">Cancellation</p>
                    <p className="whitespace-pre-wrap">{threadBooking.contract.terms.cancellation_policy}</p>
                  </div>
                )}
                {threadBooking.contract?.terms?.tech_requirements && (
                  <div className="pt-2">
                    <p className="font-medium text-zinc-700 dark:text-zinc-300">Tech requirements</p>
                    <p className="whitespace-pre-wrap">{threadBooking.contract.terms.tech_requirements}</p>
                  </div>
                )}
              </div>
            )}

            {/* Counter form */}
            {showCounterForm && canCounterOffer && (
              <div className="mt-4 space-y-3 border-t border-zinc-200/60 pt-4 dark:border-white/[0.07]">
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">
                  Propose changes
                </p>
                <Input value={counterForm.title} onChange={onCounterFieldChange("title")} placeholder="Booking title" />
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input type="datetime-local" value={counterForm.eventDate} onChange={onCounterFieldChange("eventDate")} />
                  <Input value={counterForm.city} onChange={onCounterFieldChange("city")} placeholder="City" />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input type="number" min="0" value={counterForm.payout} onChange={onCounterFieldChange("payout")} placeholder="Payout" />
                  <Input value={counterForm.currency} onChange={onCounterFieldChange("currency")} placeholder="Currency" />
                </div>
                <Input type="number" min="0" value={counterForm.depositAmount} onChange={onCounterFieldChange("depositAmount")} placeholder="Deposit amount (optional)" />
                <textarea className={textarea} rows={3} value={counterForm.notes} onChange={onCounterFieldChange("notes")} placeholder="Notes" />
                <textarea className={textarea} rows={2} value={counterForm.cancellationPolicy} onChange={onCounterFieldChange("cancellationPolicy")} placeholder="Cancellation policy" />
                <textarea className={textarea} rows={2} value={counterForm.techRequirements} onChange={onCounterFieldChange("techRequirements")} placeholder="Tech requirements" />
                <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                  <Button type="button" className="btn-secondary" onClick={() => onSetShowCounterForm(false)} disabled={isBookingAction}>Cancel</Button>
                  <Button type="button" className="btn-primary" onClick={onSubmitCounterOffer} disabled={isBookingAction}>
                    {isBookingAction ? "Sending..." : "Send counteroffer"}
                  </Button>
                </div>
              </div>
            )}

            {/* Booking creation form */}
            {showBookingForm && canCreateBooking && (
              <div className="mt-4 space-y-3 border-t border-zinc-200/60 pt-4 dark:border-white/[0.07]">
                <Input value={bookingForm.title} onChange={onBookingFieldChange("title")} placeholder="Booking title" />
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input type="datetime-local" value={bookingForm.eventDate} onChange={onBookingFieldChange("eventDate")} />
                  <Input value={bookingForm.city} onChange={onBookingFieldChange("city")} placeholder="City" />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input type="number" min="0" value={bookingForm.payout} onChange={onBookingFieldChange("payout")} placeholder="Payout" />
                  <Input value={bookingForm.currency} onChange={onBookingFieldChange("currency")} placeholder="Currency" />
                </div>
                <Input type="number" min="0" value={bookingForm.depositAmount} onChange={onBookingFieldChange("depositAmount")} placeholder="Deposit amount (optional)" />
                <textarea className={textarea} rows={3} value={bookingForm.notes} onChange={onBookingFieldChange("notes")} placeholder="Notes for the contract (set length, tech notes, etc.)" />
                <textarea className={textarea} rows={2} value={bookingForm.cancellationPolicy} onChange={onBookingFieldChange("cancellationPolicy")} placeholder="Cancellation policy" />
                <textarea className={textarea} rows={2} value={bookingForm.techRequirements} onChange={onBookingFieldChange("techRequirements")} placeholder="Tech requirements" />
                <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                  <Button type="button" className="btn-secondary" onClick={() => onSetShowBookingForm(false)} disabled={isBookingAction}>Cancel</Button>
                  <Button type="button" className="btn-primary" onClick={onCreateBookingOffer} disabled={isBookingAction}>
                    {isBookingAction ? "Sending..." : "Send booking offer"}
                  </Button>
                </div>
              </div>
            )}

            {!showBookingForm && canCreateBooking && (
              <div className="mt-3 flex justify-end">
                <Button type="button" className="btn-primary" onClick={() => onSetShowBookingForm(true)}>
                  Create booking offer
                </Button>
              </div>
            )}

            {/* Booking actions */}
            {threadBooking && (
              <div className="mt-4 flex flex-wrap gap-2">
                {acceptConfirm && (
                  <div className="w-full rounded-lg border border-emerald-200/60 bg-emerald-50 p-3 text-sm dark:border-emerald-500/20 dark:bg-emerald-500/10">
                    <p className="mb-2 text-emerald-800 dark:text-emerald-200">
                      Are you sure you want to accept this booking offer?
                    </p>
                    <div className="flex gap-2">
                      <Button type="button" className="btn-primary" disabled={isBookingAction} onClick={() => { setAcceptConfirm(false); onRespondToBooking("accept"); }}>
                        Yes, accept
                      </Button>
                      <Button type="button" className="btn-secondary" disabled={isBookingAction} onClick={() => setAcceptConfirm(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
                {canAcceptBooking && !acceptConfirm && (
                  <Button type="button" className="btn-primary" disabled={isBookingAction} onClick={() => setAcceptConfirm(true)}>
                    Accept offer
                  </Button>
                )}
                {canDeclineBooking && (
                  <Button type="button" className="btn-danger" disabled={isBookingAction} onClick={() => onRespondToBooking("decline")}>
                    Decline
                  </Button>
                )}
                {canSignContract && (
                  <Button type="button" className="btn-secondary" disabled={isBookingAction} onClick={onSignContract}>
                    {isBookingAction ? "Signing..." : "Sign contract"}
                  </Button>
                )}
                {canPayDeposit && (
                  <Button type="button" className="btn-primary" disabled={isBookingAction} onClick={onPayDeposit}>
                    {isBookingAction ? "Opening..." : "Pay deposit"}
                  </Button>
                )}
                {canCancelBooking && (
                  <Button type="button" className="btn-secondary" disabled={isBookingAction} onClick={() => onRespondToBooking("cancel")}>
                    Cancel booking
                  </Button>
                )}
                {canReopenBooking && (
                  <Button type="button" className="btn-secondary" disabled={isBookingAction} onClick={() => onRespondToBooking("reopen")}>
                    {isBookingAction ? "Re-opening..." : "Re-open offer"}
                  </Button>
                )}
                {canCounterOffer && !showCounterForm && (
                  <Button type="button" className="btn-secondary" disabled={isBookingAction} onClick={onOpenCounterForm}>
                    Propose changes
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Messages */}
        <div className="mt-4 space-y-3">
          {isThreadLoading ? (
            <p className="text-sm text-zinc-400">Loading...</p>
          ) : threadMessages.length === 0 ? (
            <p className="text-sm text-zinc-400">No messages yet.</p>
          ) : (
            <div className="flex max-h-[60vh] flex-col gap-2 overflow-auto pr-1">
              {threadMessages.map((m) => {
                const isOwn = String(m.sender_id) === String(currentUser?.id);
                return (
                  <div
                    key={m.id}
                    className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                      isOwn
                        ? "self-end bg-amber-500/15 text-amber-900 dark:text-amber-100"
                        : "self-start bg-zinc-100 text-zinc-700 dark:bg-white/[0.06] dark:text-zinc-200"
                    }`}
                  >
                    <div className="text-[11px] opacity-60 mb-0.5">
                      {new Date(m.created_at || m.timestamp || Date.now()).toLocaleDateString(undefined, { dateStyle: "medium" })}
                    </div>
                    <div className="whitespace-pre-wrap">{m.body || m.message || ""}</div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Reply box */}
        <div className="mt-4">
          <textarea
            className={`${textarea} h-24`}
            placeholder="Write a message…"
            value={threadDraft}
            onChange={(e) => onDraftChange(e.target.value)}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                e.preventDefault();
                onSendMessage();
              }
            }}
          />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-xs text-zinc-400">Ctrl+Enter to send</span>
            <Button type="button" className="btn-primary" disabled={isPostingThreadMessage} onClick={onSendMessage}>
              {isPostingThreadMessage ? "Sending..." : "Send"}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
